'use client';


import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { LogIn, Menu, ShoppingCart, UserRound } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Logo } from '@/components/icons/logo';
import { useCartItems } from '@/components/cart/cart-context';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useAppAuth } from '@/hooks/use-app-auth';
import { PUBLIC_HEADER_LINKS, ROUTES } from '@/lib/routes';
import { cn } from '@/lib/utils';

function HeaderNavLink({ href, label, pathname, onNavigate, prefetch }: { href: string; label: string; pathname: string; onNavigate?: () => void; prefetch: boolean }) {
  const isActive = href === ROUTES.home ? pathname === href : pathname === href || pathname.startsWith(`${href}/`);
  return (
    <Link
      href={href}
      prefetch={prefetch}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'relative text-sm font-medium transition-colors',
        'after:absolute after:-bottom-2 after:left-0 after:h-px after:w-full after:origin-left after:scale-x-0 after:bg-terra after:transition-transform',
        isActive ? 'text-[#8a4935] after:scale-x-100' : 'text-charcoal/80 hover:text-[#8a4935] hover:after:scale-x-100',
      )}
      onClick={onNavigate}
    >
      {label}
    </Link>
  );
}

export function Header({ prefetch = true }: { prefetch?: boolean } = {}) {
  const pathname = usePathname() || ROUTES.home;
  const { loading, session } = useAppAuth();
  const cartItems = useCartItems();
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const isResolvingSession = loading && !session;
  const accountHref = session ? ROUTES.account : `${ROUTES.login}?next=${encodeURIComponent(pathname || ROUTES.account)}`;
  const accountLabel = session ? 'Account' : 'Login';
  const headerLinks: Array<{ href: string; label: string }> = session?.canAccessAdmin && !loading
    ? [...PUBLIC_HEADER_LINKS, { href: ROUTES.admin, label: 'Admin' }, { href: ROUTES.adminResearch, label: 'Research & Stats' }]
    : [...PUBLIC_HEADER_LINKS];
  const cartQuantity = useMemo(() => cartItems.reduce((total, item) => total + item.quantity, 0), [cartItems]);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-blush/60 bg-cream/95 backdrop-blur supports-[backdrop-filter]:bg-cream/70">
      <div className="container flex h-16 max-w-7xl items-center">
        <div className="xl:hidden">
          <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Toggle menu" className="text-charcoal/80 hover:text-terra">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="border-none bg-cream pr-0">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <Logo href={ROUTES.home} prefetch={prefetch} className="mr-6 flex items-center" onClick={() => setIsSheetOpen(false)} />
              <div className="mt-6 flex flex-col space-y-4">
                {headerLinks.map((link) => (
                  <HeaderNavLink key={link.href} {...link} pathname={pathname} prefetch={prefetch} onNavigate={() => setIsSheetOpen(false)} />
                ))}
                {isResolvingSession ? (
                  <span className="text-sm font-medium text-charcoal/50" aria-live="polite">
                    Checking account...
                  </span>
                ) : (
                  <HeaderNavLink href={accountHref} label={accountLabel} pathname={pathname} prefetch={prefetch} onNavigate={() => setIsSheetOpen(false)} />
                )}
                <HeaderNavLink href={ROUTES.cart} label="Cart" pathname={pathname} prefetch={prefetch} onNavigate={() => setIsSheetOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>
        </div>

        <div className="flex flex-1 items-center justify-center xl:justify-start">
          <Logo prefetch={prefetch} />
          <nav className="ml-8 hidden items-center space-x-4 xl:flex">
            {headerLinks.map((link) => (
              <HeaderNavLink key={link.href} {...link} pathname={pathname} prefetch={prefetch} />
            ))}
          </nav>
        </div>

        <div className="flex items-center justify-end space-x-2">
          {isResolvingSession ? (
            <Button variant="ghost" disabled className="hidden text-charcoal/50 xl:inline-flex" aria-live="polite">
              Account
            </Button>
          ) : (
            <Button variant="ghost" asChild className="hidden text-charcoal/80 hover:text-terra xl:inline-flex">
              <Link href={accountHref} prefetch={prefetch} aria-current={pathname.startsWith(ROUTES.account) ? 'page' : undefined}>
                {accountLabel}
              </Link>
            </Button>
          )}
          {isResolvingSession ? (
            <Button variant="ghost" size="icon" disabled aria-label="Checking account" className="text-charcoal/50 xl:hidden">
              <UserRound className="h-5 w-5" />
            </Button>
          ) : (
            <Button variant="ghost" size="icon" asChild aria-label={accountLabel} className="text-charcoal/80 hover:text-terra xl:hidden">
              <Link href={accountHref} prefetch={prefetch}>{session ? <UserRound className="h-5 w-5" /> : <LogIn className="h-5 w-5" />}</Link>
            </Button>
          )}
          <Button variant="ghost" size="icon" asChild aria-label={`Shopping cart${cartQuantity ? `, ${cartQuantity} item${cartQuantity === 1 ? '' : 's'}` : ''}`} className="relative text-charcoal/80 hover:text-terra">
            <Link href={ROUTES.cart} prefetch={prefetch}>
              <ShoppingCart className="h-5 w-5" />
              {cartQuantity > 0 ? (
                <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-terra px-1 text-[10px] font-semibold leading-none text-white">
                  {cartQuantity > 99 ? '99+' : cartQuantity}
                </span>
              ) : null}
            </Link>
          </Button>
        </div>
      </div>
    </header>
  );
}
